import { baseApi } from "./baseApi";

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all orders with pagination
    getAllOrders: builder.query<any, { page?: number; per_page?: number; search?: string; status?: string; order_type?: string }>({
      query: ({ page = 1, per_page = 15, search, status, order_type } = {}) => {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(per_page),
        });

        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (order_type) params.append("order_type", order_type);

        return {
          url: `${process.env.REACT_APP_BACKEND_URL}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Orders"],
    }),

    // Get single order by ID
    getOrderById: builder.query<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "GET",
      }),
      providesTags: ["Orders"],
    }),

    // Update order status
    updateOrderStatus: builder.mutation<
      any,
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Orders"],
    }),

    // Day-to-day delivery (dispatch) orders. `counts` drives the per-status badges.
    getDispatchOrders: builder.query<
      any,
      { status?: string; search?: string; per_page?: number; sort?: "asc" | "desc" } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        Object.entries(args ?? {}).forEach(([key, value]) => {
          if (value !== undefined && value !== "") params.append(key, String(value));
        });
        const qs = params.toString();
        return { url: `/dispatch-orders${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["Orders"],
    }),

    getDispatchOrderById: builder.query<any, { id: string }>({
      query: ({ id }) => ({ url: `/dispatch-orders/${id}`, method: "GET" }),
      providesTags: ["Orders"],
    }),

    // Allowed at pending (keeps the original price) or customer_countered (takes the customer's price).
    acceptDispatchOrder: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({ url: `/dispatch-orders/${id}/accept`, method: "POST" }),
      invalidatesTags: ["Orders"],
    }),

    rejectDispatchOrder: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({ url: `/dispatch-orders/${id}/reject`, method: "POST" }),
      invalidatesTags: ["Orders"],
    }),

    // `description` is optional (max 1000 chars).
    counterDispatchOrder: builder.mutation<
      any,
      { id: string; offered_price: number; description?: string | null }
    >({
      query: ({ id, offered_price, description }) => ({
        url: `/dispatch-orders/${id}/counter`,
        method: "POST",
        body: { offered_price, description: description ?? null },
      }),
      invalidatesTags: ["Orders"],
    }),

    // Get company order statistics
    getCompanyOrderStats: builder.query<any, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `/companies/${companyId}/orders/stats`,
        method: "GET",
      }),
      providesTags: ["Orders"],
    }),
  }),
});

export const {
  useGetAllOrdersQuery,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useGetDispatchOrdersQuery,
  useGetDispatchOrderByIdQuery,
  useAcceptDispatchOrderMutation,
  useRejectDispatchOrderMutation,
  useCounterDispatchOrderMutation,
  useGetCompanyOrderStatsQuery,
} = ordersApi;